import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useInRouterContext, useSearchParams } from "react-router-dom";
import axios from "axios";
import { latestFirst } from "@/components/reports/latestFirst";
import { Download, Printer, RefreshCw, TriangleAlert } from "lucide-react";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { defaultExtraParams, isItemLevel, REPORT_CATALOGUE, reportsByGroup } from "@/components/reports/reportCatalogue";
import { featureAllowed, knowsModules } from "@/lib/access";
import ReportPrintDialog from "@/components/reports/ReportPrintDialog";
import MenuFilterControl, { EMPTY_MENU, menuFilterActive, menuFilterLabel } from "@/components/reports/MenuFilterControl";
import { isoBusinessDay } from "@/hooks/useReports";

/**
 * The report library.
 *
 * Picks a report out of the catalogue, asks the server for it, and renders exactly the
 * rows and totals that came back — columns are the keys of the first row, in the order
 * the server sent them. There is no client-side sample and no "generated" toast: the
 * tabs this replaced drew Butter Chicken rows and a heatmap that were the same on every
 * install, and the custom builder's Run button only called `toast.success`.
 */

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/reports`;
const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

// The business day on the restaurant's clock, as the Charts tab and the server
// count it (cut at business_day_start). toISOString() was the UTC date, so "Today"
// here and "Today" on the Charts tab could be different days (2026-10-13).
let dayStart = "06:00";
const isoDay = offsetDays => isoBusinessDay(offsetDays, dayStart);

const monthRange = (offsetMonths = 0) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - offsetMonths, 1);
  const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const end = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { from: start, to: end };
};

const PRESETS = [
  ["today", "Today", () => ({ from: isoDay(0), to: isoDay(0) })],
  ["yesterday", "Yesterday", () => ({ from: isoDay(1), to: isoDay(1) })],
  ["7d", "7 days", () => ({ from: isoDay(6), to: isoDay(0) })],
  ["30d", "30 days", () => ({ from: isoDay(29), to: isoDay(0) })],
  ["this_month", "This month", () => monthRange(0)],
  ["last_month", "Last month", () => monthRange(1)],
  ["custom", "Custom", null],
];

// Tax and short words read as the owner writes them: CGST, not "Cgst" (2026-10-24).
const SHOUTED = new Set(["cgst", "sgst", "igst", "gst", "kot", "kots", "upi", "nc", "id"]);
const humanise = key =>
  String(key)
    .split("_")
    .map(word => (SHOUTED.has(word) ? word.toUpperCase() : word))
    .join(" ")
    .replace(/^./, character => character.toUpperCase());

/**
 * Identifiers are printed, never grouped: bill 123456 is "123456" on the guest's
 * copy, so rendering it as "1,23,456" gives an owner a number they cannot search
 * for. `order_number` and `kot_number` are as much identifiers as `*_id`.
 */
const isIdColumn = key => /(^|_)(id|number|no)$/.test(String(key));

const isNumeric = value => typeof value === "number" && Number.isFinite(value);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-04-27" → "27 Apr 2026". Hand-rolled: no timezone shift, no locale surprise. */
const humanDate = iso => {
  const [year, month, day] = String(iso).split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
};

/**
 * "No rows for this range" is a lie of omission at the edges of the history.
 *
 * Two different boundaries produce an empty table and neither is a broken report:
 *
 *  1. the range sits inside the imported Petpooja years. The import is aggregate
 *     only — a day row and a (day, item) row — so a report that reads a bill LINE
 *     truthfully has nothing there, while Sales growth answers the day totals for
 *     the identical range and Item sales: day wise names the items;
 *  2. the range is before *everything* this database holds — earlier than the
 *     import itself. Pointing an owner at Sales growth here would be a second
 *     empty table, so the note has to say that instead.
 *
 * Both are decided from `historySpan`, which is the server's own
 * `history_from`/`history_to` off the last history-backed response — never from a
 * hardcoded go-live date and never from a list of slugs. `historyBacked` is
 * likewise the server's: an endpoint that returns the span fields at all is one
 * that can answer out of the history.
 */
const historyGapNote = ({ report, range, rows, span, historyBacked }) => {
  if (!range?.from || !range?.to || rows.length || !span?.from) return null;

  if (range.to < span.from) {
    return `Nothing in this system goes back that far. The earliest day on record — imported from Petpooja — is ${humanDate(span.from)}, and the range selected ends before it. Pick a range from ${humanDate(span.from)} onwards.`;
  }
  if (historyBacked || range.from > span.to) return null;

  const lead = isItemLevel(report.group)
    ? `Bill-line detail starts after the imported history (which runs to ${humanDate(span.to)})`
    : `This report reads live orders, which start after the imported history (which runs to ${humanDate(span.to)})`;
  return `${lead} — the range selected falls inside it. The Petpooja history imported for those dates is totals only: see Orders & settlement → Sales growth for the day totals, and Item sales → Item sales: day wise / Top selling items for what sold.`;
};

/** The dates in range where the import has rows that live trade displaced. */
const suppressedNote = dates => {
  if (!dates?.length) return null;
  const shown = dates.slice(0, 5).map(humanDate).join(", ");
  const rest = dates.length > 5 ? ` and ${dates.length - 5} more` : "";
  return `${dates.length} date(s) in this range also have imported Petpooja figures that are not shown, because this POS traded on them and its own record wins: ${shown}${rest}.`;
};

/** A list of dicts — the per-mode splits that ride inside `totals`. */
const isBreakdown = value =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(entry => entry && typeof entry === "object" && !Array.isArray(entry));

// How a cell reads on screen (owner, 2026-10-24, Sale Summary). Display only: the
// CSV writes the raw values.
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CODE = /^[a-z]+(_[a-z]+)+$/;
// Whole words of the column name, so "duration" is not "due" and "tax_percent" is not money.
const MONEY_COLUMN = /(^|_)(amount|total|subtotal|tax|gst|cgst|sgst|igst|cess|charge|charges|discount|redeem|round_off|price|value|sales|revenue|paid|due|tip|cost|collected|balance|refund|net|gross)(_|$)/i;
const NOT_MONEY = /(^|_)(count|qty|quantity|number|bills|orders|items|kots|covers|guests|percent|pct|rate|duration|minutes|seconds|hours|days)(_|$)/i;
const BUSINESS_TZ = "Asia/Kolkata";
// With an offset ("…+00:00", "…Z") it is an instant, shown in India time. Without
// one it is already the restaurant's wall clock, shown as sent.
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/;
const timestampText = value => {
  const naive = !HAS_OFFSET.test(value);
  return new Date(naive ? `${value}Z` : value).toLocaleString("en-GB", {
    timeZone: naive ? "UTC" : BUSINESS_TZ, day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
};
const dateText = value =>
  new Date(`${value}T00:00:00Z`).toLocaleDateString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric" });
const isMoneyColumn = key => MONEY_COLUMN.test(key) && !NOT_MONEY.test(key);

/** Money is already rupees on the wire — grouping only, never a division. */
const renderCell = (key, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isNumeric(value) && !isIdColumn(key))
    return isMoneyColumn(key)
      ? Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (typeof value === "string" && ISO_TIMESTAMP.test(value) && !Number.isNaN(Date.parse(value))) return timestampText(value);
  if (typeof value === "string" && ISO_DATE.test(value)) return dateText(value);
  if (typeof value === "string" && CODE.test(value)) {
    const words = value.replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  // The audit report's `details` is a JSONB payload and is the whole reason that
  // report exists — String() would render it as "[object Object]".
  if (typeof value === "object") {
    const json = JSON.stringify(value);
    return json === "{}" || json === "[]" ? "—" : json;
  }
  return String(value);
};

/** One entry of a `totals` breakdown as a line: "Cash · Tip amount 50". */
const breakdownText = entry => {
  const pairs = Object.entries(entry);
  if (!pairs.length) return "—";
  const [[headKey, headValue], ...rest] = pairs;
  return [
    renderCell(headKey, headValue),
    ...rest.map(([key, value]) => `${humanise(key)} ${renderCell(key, value)}`),
  ].join(" · ");
};

/** Excel and LibreOffice execute a cell that opens with one of these. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

const csvCell = value => {
  if (value === null || value === undefined) return "";
  const raw = typeof value === "object" ? JSON.stringify(value) : String(value);
  // Only staff-typed text is defused — a negative number is not a formula, and
  // quoting is no defence because the spreadsheet strips CSV quotes first.
  const text = typeof value === "string" && FORMULA_LEAD.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

/**
 * FastAPI answers a 422 with a *list of dicts*, and React throws on an object
 * child — with no ErrorBoundary in the tree that throw white-screens the whole
 * SPA. Everything that reaches `error` has to be a string.
 */
const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  const one = item => (typeof item === "string" ? item : item?.msg || JSON.stringify(item));
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map(one).join("; ");
  if (detail && typeof detail === "object") return one(detail);
  return failure?.message || "Could not load this report";
};

/** Browsers do not enforce `max` on a typed number, but the backend's `le=` does. */
const clampParam = (param, raw) => {
  const parsed = Number(raw);
  if (raw === "" || !Number.isFinite(parsed) || parsed === 0) return param.default;
  return Math.min(param.max, Math.max(param.min, Math.trunc(parsed)));
};

/**
 * `fixedSlug` locks the browser to one report and drops the library rail — the
 * Reports page's named tabs (Sales report, Item sales, Sale summary) are this same
 * table, so the three reports staff ask for by name are one tap away instead of
 * buried in a list of 35 (owner, 2026-10-13).
 */
function ReportPackBrowserWithRouter(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlReport = searchParams.get("report");
  const urlPreset = searchParams.get("preset");
  const urlFrom = searchParams.get("from");
  const urlTo = searchParams.get("to");

  const handleStateChange = useCallback(({ slug, preset, from, to }) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (slug) next.set("report", slug);
      if (preset) next.set("preset", preset);
      if (from) next.set("from", from);
      if (to) next.set("to", to);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <ReportPackBrowserBase
      {...props}
      initialSlug={urlReport}
      initialPreset={urlPreset}
      initialFrom={urlFrom}
      initialTo={urlTo}
      onStateChange={handleStateChange}
    />
  );
}

export default function ReportPackBrowser(props) {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return <ReportPackBrowserWithRouter {...props} />;
  }
  return <ReportPackBrowserBase {...props} />;
}

function ReportPackBrowserBase({
  fixedSlug = null,
  initialSlug = null,
  initialPreset = null,
  initialFrom = null,
  initialTo = null,
  onStateChange = null,
}) {
  const { token, settings, user, permissions } = usePOS();
  dayStart = settings?.business_day_start || settings?.businessDayStart || "06:00";
  // Only this person's reports (per-report switches, 2026-10-20); the server
  // refuses the others. An old cached session sees the whole library.
  const mine = useMemo(
    () => (knowsModules(user, permissions)
      ? REPORT_CATALOGUE.filter(entry => permissions.includes(`report.${entry.slug}`))
      : REPORT_CATALOGUE),
    [user, permissions]
  );
  const groups = useMemo(
    () => reportsByGroup()
      .map(group => ({ ...group, reports: group.reports.filter(entry => mine.includes(entry)) }))
      .filter(group => group.reports.length),
    [mine]
  );
  const firstSlug = (mine[0] || REPORT_CATALOGUE[0]).slug;
  const [slug, setSlug] = useState(fixedSlug || initialSlug || firstSlug);
  const [extrasInit] = useState(() => defaultExtraParams(REPORT_CATALOGUE.find(entry => entry.slug === (fixedSlug || initialSlug || firstSlug))));
  const [preset, setPreset] = useState(initialPreset || "today");
  const [customFrom, setCustomFrom] = useState(initialFrom || isoDay(6));
  const [customTo, setCustomTo] = useState(initialTo || isoDay(0));
  const [extras, setExtras] = useState(extrasInit);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  // The span the server last reported for the history tables. Kept across report
  // switches on purpose: it is a property of the database, not of the report on
  // screen, and only the three history-backed endpoints send it. The first report
  // the browser loads is one of them, so a live-only report asked about a 2024
  // range still knows where the history starts.
  const [historySpan, setHistorySpan] = useState(null);
  const [historyBacked, setHistoryBacked] = useState(false);
  const [suppressedDates, setSuppressedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloaded, setDownloaded] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [printing, setPrinting] = useState(false);
  // The item reports' menu filter. Kept across item reports: "only beverages" is
  // usually a question about the whole cluster, not one report.
  const [menu, setMenu] = useState(EMPTY_MENU);
  const [menuLevels, setMenuLevels] = useState(null);
  const requestId = useRef(0);
  // Printing is an export (the server checks reports.export, like the EOD slip).
  const canPrint = featureAllowed("reports.export", user, permissions);

  const report = useMemo(
    () => REPORT_CATALOGUE.find(entry => entry.slug === slug) || REPORT_CATALOGUE[0],
    [slug]
  );
  const wantsDates = Boolean(report?.params?.dateRange);

  const range = useMemo(() => {
    if (!wantsDates) return null;
    const chosen = PRESETS.find(([key]) => key === preset);
    return chosen?.[2] ? chosen[2]() : { from: customFrom, to: customTo };
  }, [customFrom, customTo, preset, wantsDates]);

  const selectReport = useCallback(nextSlug => {
    const next = REPORT_CATALOGUE.find(entry => entry.slug === nextSlug);
    if (!next) return;
    setSlug(nextSlug);
    onStateChange?.({ slug: nextSlug, preset, from: customFrom, to: customTo });
    setExtras(defaultExtraParams(next));
    setRows([]);
    setTotals(null);
    setSuppressedDates([]);
    setError(null);
    setDownloaded(null);
  }, [preset, customFrom, customTo, onStateChange]);

  const changePreset = useCallback(nextPreset => {
    setPreset(nextPreset);
    const chosen = PRESETS.find(([key]) => key === nextPreset);
    let nextFrom = customFrom;
    let nextTo = customTo;
    if (chosen?.[2]) {
      const computed = chosen[2]();
      nextFrom = computed.from;
      nextTo = computed.to;
      setCustomFrom(computed.from);
      setCustomTo(computed.to);
    }
    onStateChange?.({ slug, preset: nextPreset, from: nextFrom, to: nextTo });
  }, [slug, customFrom, customTo, onStateChange]);

  const changeCustomRange = useCallback((from, to) => {
    setPreset("custom");
    if (from !== undefined) setCustomFrom(from);
    if (to !== undefined) setCustomTo(to);
    onStateChange?.({ slug, preset: "custom", from: from ?? customFrom, to: to ?? customTo });
  }, [slug, customFrom, customTo, onStateChange]);

  // A locked browser follows its tab: switching Sales report -> Sale summary swaps
  // the report and keeps the chosen dates.
  useEffect(() => {
    if (fixedSlug && fixedSlug !== slug) selectReport(fixedSlug);
  }, [fixedSlug, slug, selectReport]);

  const extrasKey = JSON.stringify(extras);
  const menuKey = report.menuFilter && menuFilterActive(menu) ? JSON.stringify(menu) : "";

  useEffect(() => {
    if (!token) {
      // Never render an empty table for a request that was never made — an empty
      // state has to mean "the server answered with nothing".
      setRows([]);
      setTotals(null);
      setLoading(false);
      setError("Sign in again to read reports");
      return undefined;
    }
    if (range && (!range.from || !range.to)) {
      // A cleared date input fires onChange with "", which axios serialises as
      // `?from_date=` — a guaranteed 422. Never ask with half a range.
      setRows([]);
      setTotals(null);
      setLoading(false);
      setError("Pick a start and an end date");
      return undefined;
    }
    const id = ++requestId.current;
    let live = true;
    setLoading(true);
    setDownloaded(null);
    const params = {
      ...(range ? { from_date: range.from, to_date: range.to } : {}),
      ...JSON.parse(extrasKey),
    };
    if (menuKey) Object.assign(params, JSON.parse(menuKey));
    axios
      // Repeated keys (category=3&category=5), which is how FastAPI reads a list.
      .get(`${API}${report.path}`, menuKey ? { ...auth(token), params, paramsSerializer: { indexes: null } } : { ...auth(token), params })
      .then(response => {
        if (!live || id !== requestId.current) return;
        const body = response.data || {};
        setRows(Array.isArray(body.rows) ? latestFirst(body.rows) : []);
        setTotals(body.totals || null);
        // `history_from` present at all — even as null — is the server saying
        // "this endpoint reads the history table". A null span means the table
        // is empty, which is not the same as the report not having one.
        const carriesSpan = Object.prototype.hasOwnProperty.call(body, "history_from");
        setHistoryBacked(carriesSpan);
        if (carriesSpan && body.history_from)
          setHistorySpan({ from: body.history_from, to: body.history_to });
        setSuppressedDates(
          Array.isArray(body.suppressed_history_dates) ? body.suppressed_history_dates : []
        );
        setError(null);
      })
      .catch(failure => {
        if (!live || id !== requestId.current) return;
        setRows([]);
        setTotals(null);
        setSuppressedDates([]);
        setError(errorText(failure));
      })
      .finally(() => {
        if (!live || id !== requestId.current) return;
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [attempt, extrasKey, menuKey, range, report.path, token]);

  // The ordered union across every row, not just the first — a key that appears
  // only on a later row would otherwise vanish from the table and the export.
  const columns = useMemo(() => {
    const ordered = [];
    for (const row of rows)
      for (const key of Object.keys(row || {})) if (!ordered.includes(key)) ordered.push(key);
    return ordered;
  }, [rows]);
  const numericColumns = useMemo(
    () => new Set(columns.filter(column => rows.some(row => isNumeric(row[column])) && !isIdColumn(column))),
    [columns, rows]
  );
  const historyNote = useMemo(
    () => historyGapNote({ report, range, rows, span: historySpan, historyBacked }),
    [historyBacked, historySpan, range, report, rows]
  );
  const suppressedText = useMemo(() => suppressedNote(suppressedDates), [suppressedDates]);
  const totalsKeys = useMemo(() => (totals ? Object.keys(totals) : []), [totals]);
  const extraTotals = useMemo(
    () => totalsKeys.filter(key => !columns.includes(key)),
    [columns, totalsKeys]
  );

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: columns.map((_, index) => (index ? "minmax(96px,1fr)" : "var(--report-first-col, minmax(150px,1.6fr))")).join(" "),
      minWidth: `${Math.max(680, columns.length * 132)}px`,
    }),
    [columns]
  );

  // The report as it reads on screen, for printing: same cells, same totals, the
  // range and filters as the subtitle (owner, 2026-10-26).
  const printDoc = useMemo(() => {
    const period = range
      ? range.from === range.to ? humanDate(range.from) : `${humanDate(range.from)} – ${humanDate(range.to)}`
      : null;
    const filters = report.params.extra.map(param => {
      const value = extras[param.name];
      const option = param.options?.find(([key]) => String(key) === String(value));
      return `${param.label}: ${option ? option[1] : value}`;
    });
    return {
      title: report.title,
      subtitle: [period, ...filters, menuKey ? `Menu: ${menuFilterLabel(menu, menuLevels)}` : null].filter(Boolean).join(" · "),
      columns: columns.map(column => ({ key: column, label: humanise(column), numeric: numericColumns.has(column) })),
      rows: rows.map(row => columns.map(column => renderCell(column, row[column]))),
      totals: totalsKeys.length
        ? columns.map((column, index) => (index === 0 ? "Total" : column in totals ? renderCell(column, totals[column]) : ""))
        : null,
      extraLines: extraTotals.flatMap(key =>
        isBreakdown(totals[key])
          ? totals[key].map(entry => [humanise(key), breakdownText(entry)])
          : [[humanise(key), renderCell(key, totals[key])]]
      ),
    };
  }, [columns, extraTotals, extras, menu, menuKey, menuLevels, numericColumns, range, report, rows, totals, totalsKeys]);

  const downloadCsv = () => {
    if (!rows.length) return;
    const lines = [
      columns.map(column => csvCell(humanise(column))).join(","),
      ...rows.map(row => columns.map(column => csvCell(row[column])).join(",")),
    ];
    if (totalsKeys.length) {
      lines.push(
        columns.map((column, index) => (index === 0 ? "Total" : csvCell(totals[column]))).join(",")
      );
      // A total whose key is not a column name (`revenue` against a `line_total`
      // column, `entries`/`by_action` on the audit) has nowhere to sit in the
      // aligned row — write it out rather than dropping the money silently.
      for (const key of extraTotals) {
        if (isBreakdown(totals[key]))
          for (const entry of totals[key])
            lines.push([csvCell(humanise(key)), csvCell(breakdownText(entry))].join(","));
        else lines.push([csvCell(humanise(key)), csvCell(totals[key])].join(","));
      }
    }
    // A filtered export says so in its name: a beverages-only file must not pass for the whole menu.
    const menuPart = menuKey
      ? `-menu-${menuFilterLabel(menu, menuLevels).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`
      : "";
    const filename = `restoyaar-${report.slug}${range ? `-${range.from}-to-${range.to}` : ""}${menuPart}.csv`;
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
    setDownloaded({ filename, rows: rows.length });
  };

  return (
    <div className={`report-pack-browser${fixedSlug ? " report-pack-browser--fixed" : ""}`} data-testid="report-pack-browser">
      {!fixedSlug && <nav className="report-detail-panel report-pack-rail" data-testid="report-pack-rail" aria-label="Report library">
        <small>Report library</small>
        <h2>{mine.length} reports</h2>
        {groups.map(group => (
          <div key={group.key} className="report-pack-group" data-testid={`report-pack-group-${group.key}`}>
            <small>{group.label}</small>
            {group.reports.map(entry => (
              <button
                key={entry.slug}
                type="button"
                onClick={() => selectReport(entry.slug)}
                aria-pressed={entry.slug === slug}
                className="report-pack-item"
                data-testid={`report-pack-item-${entry.slug}`}
              >
                {entry.title}
              </button>
            ))}
          </div>
        ))}
      </nav>}

      <section className="report-detail-panel report-pack-results" data-testid="report-pack-results">
        <div className="report-detail-toolbar">
          <div>
            <small>{groups.find(group => group.key === report.group)?.label || ""}</small>
            <h2 data-testid="report-pack-title">{report.title}</h2>
          </div>
          <div className="flex items-center gap-tight shrink-0">
            {canPrint && (
              <button type="button" onClick={() => setPrinting(true)} disabled={!rows.length || loading} data-testid="report-pack-print-button"
                aria-label="Print" className="shrink-0 whitespace-nowrap">
                <Printer />
                <span className="hidden sm:inline">Print</span>
              </button>
            )}
            <button type="button" onClick={downloadCsv} disabled={!rows.length} data-testid="report-pack-download-button"
              aria-label="Download CSV" className="shrink-0 whitespace-nowrap">
              <Download />
              <span className="hidden sm:inline" data-testid="report-pack-download-label">Download CSV</span>
            </button>
          </div>
        </div>

        <div className="report-pack-controls flex flex-wrap items-center gap-snug mb-base" data-testid="report-pack-controls">
          {wantsDates && (
            <DateRangePicker
              value={{ from: range?.from, to: range?.to }}
              onChange={next => {
                if (next.preset && next.preset !== "custom") {
                  changePreset(next.preset);
                  if (next.from && next.to) {
                    setCustomFrom(next.from);
                    setCustomTo(next.to);
                  }
                } else {
                  changeCustomRange(next.from, next.to);
                }
              }}
              preset={preset}
              businessDayStart={dayStart}
              className="shrink-0"
              testId="report-pack-date-range-picker"
            />
          )}
          {wantsDates && (
            <div className="period-control flex items-center bg-muted/60 p-0.5 rounded-lg border border-border max-w-full overflow-x-auto" data-testid="report-pack-presets">
              {PRESETS.filter(([k]) => ["today", "yesterday", "7d", "30d", "custom"].includes(k)).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`h-8 px-2.5 shrink-0 whitespace-nowrap rounded-md text-meta font-medium transition-all ${preset === key ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                  onClick={() => changePreset(key)}
                  aria-pressed={preset === key}
                  data-testid={`report-pack-preset-${key}-button`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {wantsDates && preset === "custom" && (
            <div className="report-pack-range flex items-center gap-tight shrink-0">
              <DatePicker
                value={customFrom}
                max={customTo}
                onChange={next => changeCustomRange(next, customTo)}
                ariaLabel="From"
                testId="report-pack-from-date-input"
              />
              <span className="text-meta text-muted-foreground font-medium">to</span>
              <DatePicker
                value={customTo}
                min={customFrom}
                onChange={next => changeCustomRange(customFrom, next)}
                ariaLabel="To"
                testId="report-pack-to-date-input"
              />
            </div>
          )}
          {!wantsDates && (
            <p className="report-pack-note" data-testid="report-pack-no-date-note">
              Outstanding balance as it stands now — not a date range.
            </p>
          )}
          {report.menuFilter && (
            <MenuFilterControl value={menu} onChange={setMenu} token={token} onLevels={setMenuLevels} />
          )}
          {report.params.extra.map(param => (
            <label key={param.name} className="report-pack-param">
              {param.label}
              {param.type === "select" ? (
                <select
                  value={extras[param.name]}
                  onChange={event => setExtras(current => ({ ...current, [param.name]: event.target.value }))}
                  data-testid={`report-pack-param-${param.name}-select`}
                >
                  {param.options.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  value={extras[param.name]}
                  min={param.min}
                  max={param.max}
                  onChange={event => setExtras(current => ({ ...current, [param.name]: clampParam(param, event.target.value) }))}
                  data-testid={`report-pack-param-${param.name}-input`}
                />
              )}
            </label>
          ))}
        </div>

        {loading && (
          <p className="report-pack-status" data-testid="report-pack-loading" aria-live="polite">
            Loading {report.title.toLowerCase()}…
          </p>
        )}

        {!loading && error && (
          <div className="kds-error" role="alert" data-testid="report-pack-error">
            <TriangleAlert />
            <span>{error}</span>
            <Button variant="outline" onClick={() => setAttempt(count => count + 1)} data-testid="report-pack-retry-button">
              <RefreshCw />
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && !rows.length && (
          <>
            <p className="report-pack-status" data-testid="report-pack-empty">
              {wantsDates ? "No rows for this range" : "No rows"}
            </p>
            {/* An empty state names a next step (DESIGN.md §12, §13): a day with no
                bills yet is the common case, and a week usually has some. */}
            {wantsDates && (preset === "today" || preset === "yesterday") && (
              <button type="button" className="report-pack-widen" onClick={() => changePreset("7d")}
                data-testid="report-pack-widen-button">
                Show the last 7 days
              </button>
            )}
            {historyNote && (
              <p className="report-pack-longnote" data-testid="report-pack-history-note">
                {historyNote}
              </p>
            )}
          </>
        )}

        {!loading && !error && suppressedText && (
          <p className="report-pack-longnote" data-testid="report-pack-suppressed-note">
            {suppressedText}
          </p>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="report-detail-table scroll-contain-x" data-testid="report-pack-table">
            <header style={gridStyle}>
              {columns.map((column, columnIndex) => (
                <span
                  key={column}
                  className={columnIndex === 0 ? "report-cell-sticky" : undefined}
                  style={numericColumns.has(column) ? { textAlign: "right" } : undefined}
                >
                  {humanise(column)}
                </span>
              ))}
            </header>
            {rows.map((row, index) => (
              <div key={index} style={gridStyle} data-testid={`report-pack-row-${index}`}>
                {columns.map((column, columnIndex) => {
                  const text = renderCell(column, row[column]);
                  // A long list (the bill's items) is two lines with the rest on
                  // hover, not a row as tall as the screen.
                  const long = typeof text === "string" && text.length > 60;
                  return (
                    <span
                      key={column}
                      title={long ? text : undefined}
                      className={[columnIndex === 0 && "report-cell-sticky", long && "report-cell-clamp"].filter(Boolean).join(" ") || undefined}
                      style={numericColumns.has(column) ? { textAlign: "right", fontVariantNumeric: "tabular-nums" } : undefined}
                    >
                      {text}
                    </span>
                  );
                })}
              </div>
            ))}
            {totalsKeys.length > 0 && (
              <div className="report-pack-totals-row" style={gridStyle} data-testid="report-pack-totals-row">
                {columns.map((column, index) => (
                  <span key={column} style={numericColumns.has(column) ? { textAlign: "right", fontVariantNumeric: "tabular-nums" } : undefined}>
                    {index === 0 ? "Total" : column in totals ? renderCell(column, totals[column]) : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && !error && extraTotals.length > 0 && (
          <p className="report-pack-extra-totals" data-testid="report-pack-extra-totals">
            {extraTotals.map(key =>
              isBreakdown(totals[key]) ? (
                // The per-mode split is the number the drawer count needs — it
                // has to read as money, not as "[object Object]".
                <span key={key} data-testid={`report-pack-total-breakdown-${key}`}>
                  {humanise(key)}:{" "}
                  {totals[key].map((entry, index) => (
                    <b key={index}>
                      {breakdownText(entry)}
                    </b>
                  ))}
                </span>
              ) : (
                <span key={key}>
                  {humanise(key)}: <b>{renderCell(key, totals[key])}</b>
                </span>
              )
            )}
          </p>
        )}

        {printing && (
          <ReportPrintDialog
            doc={printDoc}
            slug={report.slug}
            token={token}
            restaurant={settings?.restaurantName || ""}
            onClose={() => setPrinting(false)}
          />
        )}

        {downloaded && (
          <span className="export-receipt" data-testid="report-pack-download-receipt" aria-live="polite">
            Downloaded · {downloaded.filename} · {downloaded.rows} rows
          </span>
        )}
      </section>
    </div>
  );
}
